import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AuthService from '../services/AuthService';
import { BORDER_RADIUS, SPACING_ELEMENT, SPACING_COMPONENT, SPACING_BUTTON, SPACING_CARD, SPACING_GROUP } from '../constants/layout';
import { FONT_TITLE2, FONT_TITLE2_MOBILE, FONT_BODY, FONT_FOOTNOTE } from '../constants/typography';
import { AUTH_ACTION_LABEL } from '../constants/settings';

function LoginView() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'password' | 'createAccount'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const chooseEmailFlow = (e, nextStep) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/\S+@\S+\.\S+/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setEmail(trimmed);
    setStep(nextStep);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await AuthService.signIn(email.trim(), password);
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found') {
        setError('The email or password is incorrect.');
      } else {
        setError(err.message || 'Sign-in failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    setError('');
    try {
      await AuthService.sendPasswordReset(email.trim());
    } catch {
      // Keep the response non-enumerating. Firebase may intentionally return
      // the same result for registered and unregistered addresses.
    } finally {
      setResetSent(true);
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await AuthService.createAccount(email.trim(), password, firstName, lastName || null);
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.');
      } else {
        setError(err.message || 'Failed to create account.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setError('');
    setResetSent(false);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <span className="material-icons login-icon">storefront</span>
        {step !== 'createAccount' && <h1 className="login-title">{AUTH_ACTION_LABEL}</h1>}


        {step === 'email' && (
          <form onSubmit={(event) => chooseEmailFlow(event, 'password')} className="login-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="login-input"
              autoFocus
              autoComplete="email"
            />
            {error && <p className="login-error">{error}</p>}
            <button
              type="submit"
              className="login-submit"
              disabled={loading || !email.trim()}
            >
              Sign In
            </button>
            <div className="login-links">
              <button
                type="button"
                onClick={(event) => chooseEmailFlow(event, 'createAccount')}
                className="login-link"
                disabled={loading || !email.trim()}
              >
                Create an account
              </button>
            </div>
          </form>
        )}

        {step === 'password' && (
          <form onSubmit={handleSignIn} className="login-form">
            <div className="login-email-display">{email}</div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="login-input"
              autoFocus
              autoComplete="current-password"
            />
            {error && <p className="login-error">{error}</p>}
            {resetSent && (
              <p className="login-success">
                If eligible, password reset instructions were sent.
              </p>
            )}
            <button
              type="submit"
              className="login-submit"
              disabled={loading || !password}
            >
              {loading ? 'Signing in...' : AUTH_ACTION_LABEL}
            </button>
            <div className="login-links">
              <button type="button" onClick={handleForgotPassword} className="login-link" disabled={loading}>
                Forgot password?
              </button>
              <button type="button" onClick={handleBack} className="login-link" disabled={loading}>
                Use a different email
              </button>
            </div>
          </form>
        )}

        {step === 'createAccount' && (
          <>
            <h2 className="login-create-title">Create your account</h2>
            <form onSubmit={handleCreateAccount} className="login-form">
              <div className="login-email-display">{email}</div>
              <div className="login-name-row">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  className="login-input"
                  autoFocus
                  autoComplete="given-name"
                />
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name (optional)"
                  className="login-input"
                  autoComplete="family-name"
                />
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="login-input"
                autoComplete="new-password"
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                className="login-input"
                autoComplete="new-password"
              />
              {error && <p className="login-error">{error}</p>}
              <p className="login-terms">
                By creating an account, you agree to Koral Krush's{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="login-link-inline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="login-link-inline">Privacy Policy</Link>.
              </p>
              <button
                type="submit"
                className="login-submit"
                disabled={loading || !firstName || !password || password !== confirmPassword || password.length < 6}
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
              <div className="login-links">
                <button type="button" onClick={handleBack} className="login-link" disabled={loading}>
                  Use a different email
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      <style jsx>{`
        .login-page {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 60vh;
          padding: ${SPACING_GROUP} ${SPACING_BUTTON};
        }

        .login-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          max-width: 400px;
          width: 100%;
        }

        .login-icon {
          font-size: 64px;
          color: var(--color-text-secondary);
          margin-bottom: ${SPACING_BUTTON};
        }

        .login-title {
          font-size: ${FONT_TITLE2};
          font-family: var(--font-family-base);
          font-weight: 600;
          margin: 0 0 ${SPACING_ELEMENT};
          text-align: center;
          color: var(--color-text-primary);
        }

        .login-description {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          text-align: center;
          margin: 0 0 ${SPACING_GROUP};
          line-height: 1.5;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_COMPONENT};
          width: 100%;
        }

        .login-email-display {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          text-align: center;
          padding: ${SPACING_ELEMENT} 0;
        }

        .login-input {
          width: 100%;
          padding: ${SPACING_BUTTON} ${SPACING_CARD};
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          font-size: 1rem;
          background-color: var(--color-surface-muted);
          color: var(--color-text-primary);
          box-sizing: border-box;
        }

        .login-input:focus {
          outline: none;
          border-color: var(--color-accent);
        }

        .login-submit {
          width: 100%;
          height: 48px;
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          border: none;
          border-radius: ${BORDER_RADIUS};
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s ease;
          margin-top: 4px;
        }

        .login-submit:hover:not(:disabled) {
          background-color: var(--color-accent-hover);
        }

        .login-submit:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .login-error {
          color: #ef4444;
          font-size: ${FONT_FOOTNOTE};
          margin: 0;
        }

        .login-success {
          color: #22c55e;
          font-size: ${FONT_FOOTNOTE};
          margin: 0;
        }

        .login-links {
          display: flex;
          justify-content: center;
          gap: ${SPACING_BUTTON};
          margin-top: 4px;
        }

        .login-link {
          background: none;
          border: none;
          color: var(--color-accent);
          font-size: ${FONT_FOOTNOTE};
          cursor: pointer;
          padding: 0;
        }

        .login-link:hover {
          text-decoration: underline;
        }

        .login-link:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .login-create-title {
          font-size: ${FONT_TITLE2};
          font-family: var(--font-family-base);
          font-weight: 600;
          margin: 0;
          text-align: center;
          color: var(--color-text-primary);
        }

        .login-name-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${SPACING_COMPONENT};
        }

        .login-terms {
          font-size: 0.8125rem;
          color: var(--color-text-secondary);
          text-align: center;
          margin: 0;
          line-height: 1.5;
        }

        .login-link-inline {
          color: var(--color-accent);
          text-decoration: none;
        }

        .login-link-inline:hover {
          text-decoration: underline;
        }

        @media (max-width: 480px) {
          .login-title {
            font-size: ${FONT_TITLE2_MOBILE};
          }

          .login-name-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default LoginView;
